/**
 * SPATIALx Media — motion layer.
 * Lenis smooth scroll + IntersectionObserver reveals for .sx-reveal.
 * prefers-reduced-motion: nothing runs; everything stays visible.
 *
 * MOUNT/UNMOUNT CONTRACT
 * ----------------------
 * Reveals are no longer bound once at DOMContentLoaded. mount(root) binds
 * every unbound .sx-reveal inside root; unmount(root) releases them again.
 * Both are idempotent and both are safe to call with no argument, which
 * means the whole document.
 *
 * This exists because content can arrive after first paint — the theme
 * editor re-rendering a section, and (soon) a client-side router swapping
 * <main> without a page load. Binding once and never releasing would leak
 * an observer per swap, each still holding references to detached nodes.
 * spatialx-radio.js and spatialx-home-cinema.js already work this way; this
 * file was the last one that didn't.
 *
 *   window.sxMotion.mount(el)    // observe new content
 *   window.sxMotion.unmount(el)  // release it again
 *
 * GSAP NOTE: this used to prefer a GSAP/ScrollTrigger path and fall back to
 * IntersectionObserver. GSAP + ScrollTrigger cost 115 KB on every page to
 * drive one fade, so the fallback is now the only path and the libraries are
 * gone from theme.liquid. A useful side effect: the GSAP path set inline
 * opacity/transform via gsap.set(), so anything that wanted to force an
 * element visible had to clear those inline styles as well as add .sx-in.
 * The observer path uses the class alone, so that trap no longer exists.
 */
(function () {
  'use strict';

  var root = window;

  /* Always export the contract, even on the paths where this file does no
     work. Callers gate on its existence — the client-side router refuses to
     arm without window.sxMotion.mount/unmount, and returning early without
     defining it silently disabled the router for every reduced-motion
     visitor. There is genuinely nothing to mount here (reveals are already
     visible), so no-ops are the honest implementation, not a stub. */
  function exportNoop() {
    root.sxMotion = { mount: function () {}, unmount: function () {} };
  }

  var reduced = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) { exportNoop(); return; }

  /* Nothing may hide content until we know something can reveal it again.
     body.sx-js is what drops .sx-reveal to opacity 0, so adding it before
     confirming IntersectionObserver support would leave the whole page
     blank forever on a browser that lacks it. */
  if (!('IntersectionObserver' in window)) { exportNoop(); return; }

  document.body.classList.add('sx-js');

  var lenis = null;
  if (typeof window.Lenis === 'function') {
    try {
      lenis = new window.Lenis({ duration: 0.6, smoothWheel: true });
      /* This loop is self-perpetuating: each tick's own requestAnimationFrame
         call is what schedules the next one, forever, for the life of the
         page. That means a SINGLE uncaught exception anywhere inside
         lenis.raf() — including inside any 'scroll' listener it fires
         synchronously — permanently kills the chain: no more frames ever get
         scheduled, Lenis stops animating toward its target, and mouse-wheel
         scroll appears to stop working entirely (wheel deltas keep landing in
         Lenis's internal target, they just never get applied) while native
         keyboard scrolling — which Lenis never touches — keeps working fine.
         That asymmetry is exactly what makes this failure mode so easy to
         miss and so confusing to diagnose from the outside. The try/catch
         guarantees the next frame gets scheduled regardless of what happened
         in this one. */
      var rafLenis = function (time) {
        try { lenis.raf(time); } catch (e) {}
        requestAnimationFrame(rafLenis);
      };
      requestAnimationFrame(rafLenis);
      window.sxLenis = lenis;
    } catch (e) { lenis = null; }
  }

  /* Expanding content (details/summary like LINER NOTES) grows the page after
     Lenis has measured it — remeasure on every toggle so the scroll can
     actually reach the new bottom. 'toggle' doesn't bubble, so listen in the
     capture phase. Bound to document, which the router never replaces, so
     this survives navigation without rebinding. */
  document.addEventListener('toggle', function () {
    setTimeout(function () {
      try { if (lenis && lenis.resize) { lenis.resize(); } } catch (e) {}
    }, 60);
  }, true);

  var EASE_CSS = 'cubic-bezier(0.16, 1, 0.3, 1)';
  var BOUND_ATTR = 'sxRevealBound';
  var io = null;
  var styled = false;

  function ensureStyle() {
    if (styled) { return; }
    styled = true;
    var style = document.createElement('style');
    style.textContent =
      'body.sx-js .sx-reveal{opacity:0;transform:translateY(12px);' +
      'transition:opacity .4s ' + EASE_CSS + ',transform .4s ' + EASE_CSS + '}' +
      'body.sx-js .sx-reveal.sx-in{opacity:1;transform:none}';
    document.head.appendChild(style);
  }

  function ensureObserver() {
    if (io) { return io; }
    io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) { return; }
        en.target.classList.add('sx-in');
        /* Reveal is one-way: stop watching the moment it lands so a long
           page doesn't keep a live entry per element it already showed. */
        obs.unobserve(en.target);
        delete en.target.dataset[BOUND_ATTR];
      });
    }, { rootMargin: '0px 0px -15% 0px' });
    return io;
  }

  function each(root, fn) {
    var scope = root && root.querySelectorAll ? root : document;
    var list = scope.querySelectorAll('.sx-reveal');
    for (var i = 0; i < list.length; i++) { fn(list[i]); }
    /* querySelectorAll only looks at descendants — if the router hands us
       the swapped element itself and that element is a .sx-reveal, it would
       otherwise be skipped. */
    if (scope !== document && scope.classList &&
        scope.classList.contains('sx-reveal')) { fn(scope); }
  }

  function mount(root) {
    ensureStyle();
    var obs = ensureObserver();
    each(root, function (el) {
      /* Already landed, or already being watched — either way, leave it.
         Re-observing would be harmless but re-revealing would replay the
         animation on content the visitor has already seen. */
      if (el.classList.contains('sx-in')) { return; }
      if (el.dataset[BOUND_ATTR] === 'true') { return; }
      el.dataset[BOUND_ATTR] = 'true';
      obs.observe(el);
    });
  }

  function unmount(root) {
    if (!io) { return; }
    if (!root) {
      io.disconnect();
      io = null;
      return;
    }
    each(root, function (el) {
      if (el.dataset[BOUND_ATTR] !== 'true') { return; }
      io.unobserve(el);
      delete el.dataset[BOUND_ATTR];
    });
  }

  window.sxMotion = { mount: mount, unmount: unmount };

  /* Theme editor parity, matching spatialx-radio.js and
     spatialx-home-cinema.js: a re-rendered section arrives unbound. */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) { mount(e.target); }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target) { unmount(e.target); }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mount(); });
  } else {
    mount();
  }
}());
